/* globals globalNavigate */
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import EditIcon from "@mui/icons-material/Edit";
import Modal from "common/Modal";
import { checkNothingPending, isNameValid, getProfiles } from "common/api";

const EditNameButton = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 20px 5px 5px;
  margin: 0 0px 0 10px;
  svg {
    margin-right: 5px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const NameWrapper = styled.span`
  font-family: "AllianceNo2-SemiBold" !important;
  font-weight: 100;
  padding: 0 10px;
`;

export const parseProfileLines = ({ response, name, type }) => {
  const lines = response.trim().split("\n");
  const { profile } = lines.reduce(
    (acc, line) => {
      if (line === `profile ${type} ${name}`) {
        acc.inProfile = true;
      } else if (acc.inProfile && line.match(/^\s+/)) {
        acc.profile.settings.push(line);
      } else {
        acc.inProfile = false;
        const lineAttrs = line.trim().split(/\s+/);
        if (line.startsWith(`rule `) && lineAttrs.includes(name) && lineAttrs[lineAttrs.length - 1] !== name) {
          acc.profile.rules.push(line);
        }
      }
      return acc;
    },
    {
      inProfile: false,
      profile: { name, settings: [], rules: [], type },
    },
  );

  return { profile };
};

export const showConfig = (nameInit, type) =>
  ifCl
    .run("show config running all")
    .then((response) =>
      parseProfileLines({ response, name: nameInit, type }),
    );

const getSettingIndent = (setting) => {
  if(!setting) return undefined;
  const match = setting.match(/^\s*/);
  return match ? match[0].length : 0;
};

export const updateProfile = ({ name, nameNew, type, settings, rules }) => {
  const cmdsRules =
    rules.length > 0
      ? rules.map((rule) => {
          const index = rule.indexOf(name);
          const newRule =
            index !== -1
              ? rule.substring(0, index) +
                nameNew +
                rule.substring(index + name.length)
              : rule;
          return "no " + rule + "\n" + newRule;
        })
      : [];
  const cmdsSettings = settings.reduce((acc, setting, index) => {
    const settingIndent = getSettingIndent(setting);
    while (acc.indent > settingIndent) {
      acc.cmds.push("exit");
      acc.indent -= 2;
    }
    acc.cmds.push(setting.trim());
    if (setting.trim().startsWith("address") && getSettingIndent(settings[index+1]) !== 4){
      acc.cmds.push("exit");
    }
    acc.indent = settingIndent;
    return acc;
  }, { cmds: [], indent: 0 });

  const cmds = [
    `configure`,
    `no profile ${type} ${name}`,
    `profile ${type} ${nameNew}`,
    ...cmdsSettings.cmds.length > 0 ? cmdsSettings.cmds : [],
    ...Array(cmdsSettings.indent / 2 === 0 ? 1 : cmdsSettings.indent / 2).fill("exit"),
    ...(rules.length > 0 ? cmdsRules : []),
  ];

  return ifCl.run(`${cmds.join("\n")}\ncommit\n`, /*is batch*/ true);
};

export const NameModal = ({
  onClose,
  name: nameInit,
  type,
  profiles,
  returnView,
}) => {
  const [open, setOpen] = useState(true);
  const [name, setName] = useState(nameInit);
  const [errorFooter, setErrorFooter] = useState(null);

  if (!open) {
    return null;
  }

  const handleApply = () => {
    if (!name) {
      setErrorFooter("Empty name");
      return;
    }
    if (!isNameValid(name)) {
      setErrorFooter("Invalid name");
      return;
    }
    const profilesSet = new Set(profiles);
    if (profilesSet.has(name)) {
      setErrorFooter("Duplicated profile name");
      return;
    }
    checkNothingPending()
      .then(() => {
        return showConfig(nameInit, type);
      })
      .then((response) => {
        const { profile } = response;
        return updateProfile({
          name: nameInit,
          nameNew: name,
          type,
          settings: profile.settings,
          rules: profile.rules,
        }).then((response) => {
          if (response) {
            ifCl.run("configure\nclear config changes\n", /*is batch*/ true);
          }
          return Promise.resolve();
        });
      })
      .then(() => {
        onClose();
        globalNavigate(returnView, {
          edit: name,
        });
      })
      .catch((error) => {
        ifCl.run("configure\nclear config changes\n", /*is batch*/ true);
        setErrorFooter(error.message);
      });
  };

  return (
    <Modal
      title="Edit Policy Name"
      superIcon="edit"
      onClose={onClose}
      closeLabel="CANCEL"
      content={() => (
        <div className="input-group">
          <span className="input-group-addon" title="Policy name">
            Name
          </span>
          <input
            id="poliName"
            type="text"
            size="55"
            style={{ borderBottom: "1px solid #ddd" }}
            className="align-left"
            onChange={({ target }) => {
              setErrorFooter(null);
              setName(target.value);
            }}
            value={name}
          />
        </div>
      )}
      onApply={handleApply}
      applyDisabled={nameInit === name}
      applyLabel="OK"
      footerMessage={
        errorFooter ? (
          <span className="modal-err-msg color-red align-left">
            <strong>Error:</strong> {errorFooter}
          </span>
        ) : null
      }
    />
  );
};

function EditNameProfile({ name, type, returnView }) {
  const [displayNameModal, setDisplayNameModal] = useState(false);
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    getProfiles().then((profiles) => {
      setProfiles(profiles);
    });
  }, []);

  return (
    <Wrapper>
      <div>
        Name: <NameWrapper>{name}</NameWrapper>
      </div>
      <EditNameButton
        onClick={() => setDisplayNameModal(true)}
        className="btn btn-link waves-effect"
      >
        <EditIcon />
        Edit Name
      </EditNameButton>
      {displayNameModal ? (
        <NameModal
          type={type}
          name={name}
          onClose={() => setDisplayNameModal(false)}
          profiles={profiles}
          returnView={returnView}
        />
      ) : null}
    </Wrapper>
  );
}

export default EditNameProfile;